// eslint-disable-next-line no-restricted-imports
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { decode } from 'jsonwebtoken'
import { getRawAuthToken, storeMakerToken } from 'utils/auth'

const backendURL = process.env['REACT_APP_BACKEND_URL']
if (!backendURL) throw new Error('REACT_APP_BACKEND_URL not defined')
const marketplaceURL = process.env['REACT_APP_COMPONENT_REGISTRY']
if (!marketplaceURL) console.warn('REACT_APP_COMPONENT_REGISTRY not defined')
const publicApiURL = process.env['REACT_APP_ADALO_API']
if (!publicApiURL) console.warn('REACT_APP_ADALO_API not defined')
const aiAssistantURL = process.env['REACT_APP_AI_ASSISTANT_SERVICE_URL']
if (!aiAssistantURL) {
  console.warn('REACT_APP_AI_ASSISTANT_SERVICE_URL not defined')
}

export type { AxiosResponse }
// eslint-disable-next-line @typescript-eslint/unbound-method
export const isAxiosError = axios.isAxiosError

const TOKEN_REFRESH_THRESHOLD_SECONDS = 300

interface JWTPayload {
  exp?: number
  [key: string]: unknown
}

interface RefreshTokenResponse {
  accessToken?: string
  [key: string]: unknown
}

const isTokenExpiringSoon = (token: string): boolean => {
  const payload = decode(token) as JWTPayload
  if (!payload?.exp) return false

  const expirationTime = payload.exp * 1000
  const now = Date.now()
  const thresholdTime = now + TOKEN_REFRESH_THRESHOLD_SECONDS * 1000

  return expirationTime <= thresholdTime
}

const getNewAccessToken = async (): Promise<string | null> => {
  try {
    const response = await axios.post<RefreshTokenResponse>(
      '/auth/refresh',
      {},
      {
        baseURL: backendURL,
        withCredentials: true,
      }
    )

    const newToken = response.data?.accessToken
    if (newToken) {
      storeMakerToken(newToken)

      return newToken
    }

    return null
  } catch (error) {
    console.error('Failed to refresh token:', error)

    return null
  }
}

const addAuthenticationHeader = (
  config: AxiosRequestConfig,
  token: string | null
): void => {
  if (token && config.headers) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, no-param-reassign
    config.headers['x-proton-auth'] = token
  }
}

const createAuthRequestInterceptor = () => {
  return async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    const currentToken = getRawAuthToken()

    if (currentToken && isTokenExpiringSoon(currentToken)) {
      console.log(`Expired Access Token: ...${currentToken.slice(-5)}`)
      const refreshedToken = await getNewAccessToken()
      addAuthenticationHeader(config, refreshedToken)

      return config
    }

    addAuthenticationHeader(config, currentToken)

    return config
  }
}

const createAuthedAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({ baseURL })
  instance.interceptors.request.use(createAuthRequestInterceptor())

  return instance
}

const createAuthedAxiosBackendInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
  })
  instance.interceptors.request.use(createAuthRequestInterceptor())

  return instance
}

const createAuthedAIAssistantInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
  })
  instance.interceptors.request.use(createAuthRequestInterceptor())

  return instance
}

// for Adalo backend API calls
export const adaloBackendAxios = createAuthedAxiosBackendInstance(backendURL)
// for the marketplace API calls aka component registry
export const marketplaceAxios = createAuthedAxiosInstance(marketplaceURL || '')
// for the public API calls aka collections API & notifications push API
export const publicApiAxios = createAuthedAxiosInstance(publicApiURL || '')

export const aiAssistantAxios = createAuthedAIAssistantInstance(
  aiAssistantURL || ''
)

// for any other API calls
export const defaultAxios = axios.create()
